import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createWorkflowApp, getWorkflowAppByWorkflowId, updateWorkflowApp } from '@/api/workflowApps.api';
import type { CreateWorkflowAppRequestDto, UpdateWorkflowAppRequestDto, WorkflowApp } from '@/types/workflow-app.types';
import type { NodeId } from 'web';

export const WORKFLOW_APP_QUERY_KEY_ROOT = ['workflowApp'] as const;

export function getWorkflowAppByWorkflowIdQueryKey(workflowId: string | undefined) {
  return [...WORKFLOW_APP_QUERY_KEY_ROOT, 'byWorkflowId', workflowId] as const;
}

export function useWorkflowAppByWorkflowIdQuery(workflowId: string | undefined) {
  return useQuery({
    queryKey: getWorkflowAppByWorkflowIdQueryKey(workflowId),
    queryFn: () => getWorkflowAppByWorkflowId(workflowId!),
    enabled: Boolean(workflowId),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useCreateWorkflowAppMutation() {
  const queryClient = useQueryClient();

  return useMutation<WorkflowApp, Error, CreateWorkflowAppRequestDto>({
    mutationFn: createWorkflowApp,
    onSuccess: (created) => {
      queryClient.setQueryData(getWorkflowAppByWorkflowIdQueryKey(created.workflowId), created);
    },
  });
}

export function useUpdateWorkflowAppMutation() {
  const queryClient = useQueryClient();

  return useMutation<WorkflowApp, Error, { workflowAppId: string; patch: UpdateWorkflowAppRequestDto }>({
    mutationFn: ({ workflowAppId, patch }) => updateWorkflowApp(workflowAppId, patch),
    onSuccess: (updated) => {
      queryClient.setQueryData(getWorkflowAppByWorkflowIdQueryKey(updated.workflowId), updated);
    },
  });
}

export function useAppOutputMutations(workflowApp: WorkflowApp | null | undefined) {
  const updateWorkflowAppMutation = useUpdateWorkflowAppMutation();

  const setAsOutput = useCallback(
    (nodeId: NodeId) => {
      if (!workflowApp) {
        return;
      }

      const outputs = workflowApp.draftOverrides.outputs;
      if (outputs.some((output) => output.nodeId === nodeId)) {
        return;
      }

      updateWorkflowAppMutation.mutate({
        workflowAppId: workflowApp.id,
        patch: {
          draftOverrides: {
            ...workflowApp.draftOverrides,
            outputs: [...outputs, { nodeId, order: outputs.length }],
          },
        },
      });
    },
    [updateWorkflowAppMutation, workflowApp],
  );

  const removeOutput = useCallback(
    (nodeId: NodeId) => {
      if (!workflowApp) {
        return;
      }

      const nextOutputs = [...workflowApp.draftOverrides.outputs]
        .sort((a, b) => a.order - b.order)
        .filter((output) => output.nodeId !== nodeId)
        .map((output, order) => ({ ...output, order }));

      updateWorkflowAppMutation.mutate({
        workflowAppId: workflowApp.id,
        patch: {
          draftOverrides: {
            ...workflowApp.draftOverrides,
            outputs: nextOutputs,
          },
        },
      });
    },
    [updateWorkflowAppMutation, workflowApp],
  );

  return {
    setAsOutput,
    removeOutput,
    isUpdatingOutput: updateWorkflowAppMutation.isPending,
  };
}
